// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — ProfitDrillDown
// Detailed view for Net Profit KPI
// ══════════════════════════════════════════════════════════════════════════════

import {
  AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  TrendingUp, TrendingDown, Wallet, CreditCard,
  ArrowLeft, ArrowRight
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { Card, Button, Grid } from '@components/ui/Primitives';
import { StatCard } from '@components/ui/StatCard';

export function ProfitDrillDown({ data, onBack }) {
  const { theme } = useTheme();
  const { t, lang, isRTL } = useLang();

  const sales = data?.sales || [];

  // Prepare flow data: group sales by week
  const flowData = [...sales]
    .slice(0, 84)
    .reverse()
    .reduce((acc, sale, idx) => {
      const weekNum = Math.floor(idx / 7);
      const revenue = parseFloat(sale.total_revenue || 0);
      const expenses = revenue * 0.62;
      if (!acc[weekNum]) {
        acc[weekNum] = {
          week: lang === 'ar' ? `أسبوع ${weekNum + 1}` : `W${weekNum + 1}`,
          income: 0,
          expenses: 0
        };
      }
      acc[weekNum].income += revenue;
      acc[weekNum].expenses += expenses;
      return acc;
    }, [])
    .map(w => ({
      ...w,
      profit: w.income - w.expenses
    }));

  const totalIncome = flowData.reduce((s, d) => s + d.income, 0);
  const totalExpenses = flowData.reduce((s, d) => s + d.expenses, 0);
  const netProfit = totalIncome - totalExpenses;
  const grossMargin = totalIncome > 0 ? ((netProfit / totalIncome) * 100).toFixed(1) : 0;

  // Expense breakdown
  const expenseBreakdown = [
    { name_ar: 'مواد خام', name_en: 'Raw Materials', value: 42 },
    { name_ar: 'عمالة', name_en: 'Labor', value: 28 },
    { name_ar: 'إيجار', name_en: 'Rent', value: 15 },
    { name_ar: 'مرافق', name_en: 'Utilities', value: 8 },
    { name_ar: 'تسويق', name_en: 'Marketing', value: 4 },
    { name_ar: 'أخرى', name_en: 'Other', value: 3 }
  ];

  const tooltipStyle = {
    background: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    fontSize: 12,
    padding: '8px 12px'
  };

  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} style={{ marginBottom: 20 }}>
        <BackIcon size={14} /> {t.back}
      </Button>

      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: theme.text, letterSpacing: -0.3 }}>
          {t.profitTitle}
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
          {t.profitDesc}
        </p>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Grid min={200}>
          <StatCard
            icon={<TrendingUp size={20} color={theme.success} />}
            label={lang === 'ar' ? 'الإيرادات' : 'Income'}
            value={`${t.sar} ${(totalIncome / 1000).toFixed(0)}K`}
            trend="+9.3%"
            trendDirection="up"
            accentColor={theme.success}
          />
          <StatCard
            icon={<TrendingDown size={20} color={theme.danger} />}
            label={lang === 'ar' ? 'المصروفات' : 'Expenses'}
            value={`${t.sar} ${(totalExpenses / 1000).toFixed(0)}K`}
            accentColor={theme.danger}
          />
          <StatCard
            icon={<Wallet size={20} color={theme.primary} />}
            label={t.netProfit}
            value={`${t.sar} ${(netProfit / 1000).toFixed(0)}K`}
            trend="+15%"
            trendDirection="up"
          />
          <StatCard
            icon={<CreditCard size={20} color={theme.info} />}
            label={t.grossMargin}
            value={`${grossMargin}%`}
            accentColor={theme.info}
          />
        </Grid>
      </div>

      <Grid min={380}>
        <Card style={{ padding: 22 }}>
          <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: theme.text }}>
            {lang === 'ar' ? 'اتجاه الربح' : 'Profit Trend'}
          </h4>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={flowData}>
              <defs>
                <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.primary} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={theme.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
              <XAxis dataKey="week" tick={{ fill: theme.textTertiary, fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: theme.textTertiary, fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area
                type="monotone"
                dataKey="profit"
                stroke={theme.primary}
                fill="url(#profitGrad)"
                strokeWidth={2}
                name={t.netProfit}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card style={{ padding: 22 }}>
          <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: theme.text }}>
            {t.expenseBreakdown}
          </h4>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={expenseBreakdown.map(e => ({
                  name: lang === 'ar' ? e.name_ar : e.name_en,
                  value: e.value
                }))}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={100}
                paddingAngle={3}
                dataKey="value"
              >
                {theme.chartColors.map((c, i) => (
                  <Cell key={i} fill={c} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value) => `${value}%`}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </Grid>
    </div>
  );
}
